import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { Easings, TweenGroup } from '../../core/Tween';
import { waitForNextVideoFrame } from '../slot/waitForNextVideoFrame';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface ChestWidgetOptions {
  /** Chest width in px. Default 220. Height auto-derived from a 3:2 ratio
   * (slightly squat to read as "chest" rather than "box"). */
  width?: number;
  /** Body fill colour for the procedural Graphics fallback. Default warm
   * wood-brown. Ignored when sprite aliases are provided + loaded. */
  bodyColor?: number;
  /** Trim / band colour for the procedural Graphics fallback. Default
   * brass. Ignored when sprite aliases are provided + loaded. */
  trimColor?: number;
  /** Glow ring colour for the open burst. Default gold. Always used —
   * the glow lives behind the chest art regardless of render mode. */
  glowColor?: number;
  /** Per-chest hint text. Default `'TAP TO OPEN'`. Pass `null` to skip
   * the hint — the 3-chest pick scene uses a single row-level prompt
   * instead so we don't render three identical labels stacked. */
  hint?: string | null;
  /** Asset-loader alias for the closed-state sprite. Resolved via
   * {@link AssetLoader#getTexture}; falls back to the procedural
   * Graphics chest when null/missing/unloaded. Asset brief pack 3 §30
   * registers this as `pickem:chest_closed`. */
  closedTextureAlias?: string | null;
  /** Asset-loader alias for the open-state hold sprite (the static
   * trophy pose shown after the open animation finishes). Falls back to
   * the procedural Graphics lid-rotation when null/missing/unloaded.
   * Asset brief pack 3 §30 registers this as `pickem:chest_open`. */
  openTextureAlias?: string | null;
  /** Asset-loader alias for the open-animation video. Resolved lazily
   * via {@link AssetLoader#getVideo} on first {@link open} call. Asset
   * brief pack 3 §30 registers this as `pickem:chest_open_video`. When
   * the video isn't available the widget cross-fades closedSprite →
   * openSprite (or runs the procedural lid rotation if no openTexture
   * either). */
  openVideoAlias?: string | null;
  onTap: () => void;
}

const DEFAULT_BODY = 0x8b5a2b;
const DEFAULT_TRIM = 0xd4a850;
const DEFAULT_GLOW = 0xffe066;

/**
 * Treasure-chest widget — sprite-driven when commissioned art is wired
 * via {@link ChestWidgetOptions#closedTextureAlias} +
 * {@link ChestWidgetOptions#openTextureAlias} +
 * {@link ChestWidgetOptions#openVideoAlias}, with a procedural
 * {@link Graphics} fallback for any of those that's missing.
 *
 * <p>Render layers (back→front, all centred on the widget's origin):
 * <ol>
 *   <li>Glow ring — invisible until {@link open} fires
 *   <li>Procedural body + lid (only painted in fallback mode)
 *   <li>Closed sprite (only when the alias resolves)
 *   <li>Open sprite (hidden until the open animation completes)
 *   <li>Video sprite (hidden, swapped in only during the open animation)
 *   <li>Hint text — fades out as soon as {@link open} fires
 * </ol>
 *
 * <p>{@link open}'s async behaviour by available assets:
 * <ul>
 *   <li><b>Video</b> available → swap closed→video, play once, on
 *       {@code ended} swap video→openSprite. Resolves on the video
 *       end-frame paint (matching the
 *       {@link ../slot/SlotSymbol#playWinAnimation} pattern).
 *   <li><b>Open texture only</b> → cross-fade closed→open over ~700ms.
 *   <li><b>Neither</b> → run the legacy procedural lid rotation.
 * </ul>
 *
 * <p>The widget itself doesn't fire particles or coin animations — the
 * host scene composes those over the chest centre after {@link open}
 * completes.
 */
export class ChestWidget extends Container {
  private readonly w: number;
  private readonly h: number;
  private readonly glow: Graphics;
  /** Procedural body + lid Graphics — present only in the fallback
   * render mode (no sprite aliases or aliases didn't resolve). */
  private readonly body: Graphics | null;
  private readonly lidContainer: Container | null;
  private readonly lid: Graphics | null;
  /** Sprite render path — present only when the alias resolved at
   * construction time AND the texture was loaded. */
  private readonly closedSprite: Sprite | null;
  private readonly openSprite: Sprite | null;
  private videoSprite: Sprite | null = null;
  private readonly hintText: Text | null;
  private readonly tweens = new TweenGroup();
  private opened = false;
  private readonly onTapCallback: () => void;
  private readonly options: ChestWidgetOptions;

  constructor(options: ChestWidgetOptions) {
    super();
    this.w = options.width ?? 220;
    this.h = Math.round(this.w * (2 / 3));
    this.options = options;

    const bodyColor = options.bodyColor ?? DEFAULT_BODY;
    const trimColor = options.trimColor ?? DEFAULT_TRIM;
    const glowColor = options.glowColor ?? DEFAULT_GLOW;
    this.onTapCallback = options.onTap;

    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Glow ring sits behind everything — invisible until open() fires.
    this.glow = new Graphics();
    this.glow.alpha = 0;
    this.addChild(this.glow);
    this.paintGlow(glowColor);

    const closedTexture = options.closedTextureAlias
      ? AssetLoader.shared.getTexture(options.closedTextureAlias)
      : null;
    const openTexture = options.openTextureAlias
      ? AssetLoader.shared.getTexture(options.openTextureAlias)
      : null;

    if (closedTexture) {
      // Sprite render path — commissioned art is loaded. The procedural
      // Graphics body + lid stay null; the closed sprite IS the chest
      // until open() fires.
      this.body = null;
      this.lidContainer = null;
      this.lid = null;
      this.closedSprite = createCenteredSprite(closedTexture, this.w);
      this.addChild(this.closedSprite);
      // Open sprite is built up-front so the post-animation swap is
      // instantaneous and we never see a missing-frame flash.
      this.openSprite = openTexture
        ? createCenteredSprite(openTexture, this.w)
        : null;
      if (this.openSprite) {
        this.openSprite.visible = false;
        this.addChild(this.openSprite);
      }
    } else {
      // Procedural fallback — same draw code as the v0 widget.
      this.closedSprite = null;
      this.openSprite = null;
      this.body = new Graphics();
      this.paintBody(bodyColor, trimColor);
      this.addChild(this.body);
      // Lid pivots from its rear-top edge so rotation reads as "real
      // chest opening" rather than "lid flopping forward".
      this.lidContainer = new Container();
      this.lidContainer.x = 0;
      this.lidContainer.y = -this.h / 2;
      this.addChild(this.lidContainer);
      this.lid = new Graphics();
      this.paintLid(bodyColor, trimColor);
      this.lidContainer.addChild(this.lid);
    }

    const hintLabel = options.hint === undefined ? 'TAP TO OPEN' : options.hint;
    if (hintLabel !== null) {
      this.hintText = new Text({
        text: hintLabel,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: 14,
          fontWeight: '900',
          fill: 0xffffff,
          letterSpacing: 3,
        },
      });
      this.hintText.anchor.set(0.5);
      this.hintText.y = this.h / 2 + 28;
      this.addChild(this.hintText);
    } else {
      this.hintText = null;
    }

    this.on('pointertap', () => {
      if (this.opened) return;
      this.onTapCallback();
    });
  }

  /** True iff {@link open} has already fired. Hosts gate further taps on this. */
  get isOpen(): boolean {
    return this.opened;
  }

  /** Run the chest-opening animation. Resolves after the lid has fully
   * rotated up (procedural mode), the cross-fade lands (sprite-only
   * mode), or the video reaches its end frame (video mode). Idempotent
   * — repeat calls resolve immediately. */
  async open(): Promise<void> {
    if (this.opened) return;
    this.opened = true;
    this.eventMode = 'auto';
    this.cursor = 'default';

    // Hint fades out the moment the chest starts opening.
    this.tweens.start({
      durationMs: 220,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        if (this.hintText) this.hintText.alpha = 1 - p;
      },
    });

    // Glow ring blooms in over the open animation; held briefly at full
    // bloom then fades. Same envelope across all render paths so the
    // chest feels equivalently celebratory whether sprite or procedural.
    this.tweens.start({
      durationMs: 600,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.glow.alpha = p * 0.85;
        this.glow.scale.set(0.6 + p * 0.6);
      },
      onComplete: () => {
        this.tweens.start({
          durationMs: 800,
          easing: Easings.easeOutCubic,
          onUpdate: (p) => {
            this.glow.alpha = 0.85 * (1 - p);
          },
        });
      },
    });

    // Pick the open animation by what's available, in priority order:
    //   1. Veo video (best fidelity)
    //   2. Static closed → open cross-fade (still painterly, no procedural)
    //   3. Procedural lid-rotation (the v0 fallback)
    if (this.closedSprite && this.options.openVideoAlias) {
      const video = AssetLoader.shared.getVideo(this.options.openVideoAlias);
      if (video) {
        await this.playOpenVideo(video);
        return;
      }
    }
    if (this.closedSprite && this.openSprite) {
      await this.crossfadeClosedToOpen();
      return;
    }
    if (this.lidContainer) {
      await this.tweens.startAsync({
        durationMs: 700,
        easing: Easings.easeOutBack,
        onUpdate: (p) => {
          if (this.lidContainer) {
            this.lidContainer.rotation = -p * (Math.PI * 0.6);
          }
        },
      });
    }
  }

  /** Cross-fade the closed sprite out and the open sprite in over
   * ~700ms with the easeOut curve the procedural lid rotation used —
   * the cadence matches even though the visual is different. */
  private crossfadeClosedToOpen(): Promise<void> {
    if (!this.closedSprite || !this.openSprite) return Promise.resolve();
    this.openSprite.alpha = 0;
    this.openSprite.visible = true;
    return this.tweens.startAsync({
      durationMs: 700,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        if (!this.closedSprite || !this.openSprite) return;
        this.closedSprite.alpha = 1 - p;
        this.openSprite.alpha = p;
      },
      onComplete: () => {
        if (this.closedSprite) this.closedSprite.visible = false;
      },
    });
  }

  /** Play the opening video as a sprite swap over the closed chest.
   * Mirrors {@link ../slot/SlotSymbol#playWinVideo}: build the video
   * sprite lazily, wait for the first painted frame before flipping
   * visibility (so the seek-to-0 + decode warm-up doesn't leak through
   * as a black flash), then swap to the static open sprite on `ended`
   * (Pixi loses the alpha channel once the browser pauses at the end
   * frame, so holding the video as the trophy pose looks broken). */
  private async playOpenVideo(loaded: {
    texture: Texture;
    element: HTMLVideoElement;
  }): Promise<void> {
    if (!this.closedSprite) return;
    if (!this.videoSprite) {
      this.videoSprite = createCenteredSprite(loaded.texture, this.w);
      this.videoSprite.visible = false;
      // Insert below openSprite (if present) and above closedSprite so
      // the still end-frame can pop on top when the video finishes.
      const insertIdx = this.openSprite
        ? this.getChildIndex(this.openSprite)
        : this.children.length;
      this.addChildAt(this.videoSprite, insertIdx);
    } else {
      this.videoSprite.texture = loaded.texture;
      this.videoSprite.visible = false;
    }

    const element = loaded.element;
    if (element.paused || element.ended) {
      element.currentTime = 0;
      element.playbackRate = 1.0;
      void element.play().catch(() => undefined);
    }

    // Hold the closed sprite until the first video frame paints —
    // identical reasoning to SlotSymbol.playWinVideo: the seek-to-0 +
    // decode window leaves the texture as a 1×1 black placeholder
    // which, if shown, flashes through where the chest used to be.
    await waitForNextVideoFrame(element);

    // Re-size now that we know the painted dimensions; the texture's
    // orig was 1×1 before the first frame painted, so width-set at
    // construction time bakes a wrong scale.
    const intrinsic =
      element.videoWidth || this.videoSprite.texture.orig.width || 1;
    this.videoSprite.scale.set(this.w / intrinsic);

    this.videoSprite.visible = true;
    this.closedSprite.visible = false;

    return new Promise<void>((resolve) => {
      const swapToStatic = (): void => {
        element.removeEventListener('ended', swapToStatic);
        if (this.videoSprite) this.videoSprite.visible = false;
        if (this.openSprite) {
          this.openSprite.alpha = 1;
          this.openSprite.visible = true;
        } else if (this.closedSprite) {
          // No open texture wired — leave the closed sprite hidden and
          // the video frame as the last visible state. Better than
          // flashing back to closed; consumer should ship both sprites.
          this.closedSprite.visible = false;
        }
        resolve();
      };
      element.addEventListener('ended', swapToStatic);
    });
  }

  destroyChest(): void {
    this.tweens.cancelAll();
  }

  private paintGlow(color: number): void {
    const radius = this.w * 0.85;
    this.glow.clear();
    // Concentric rings — outer is faint, inner is brighter; alpha on the
    // container ramps via tween so this composes into a halo.
    this.glow
      .circle(0, 0, radius)
      .fill({ color, alpha: 0.18 })
      .circle(0, 0, radius * 0.7)
      .fill({ color, alpha: 0.32 })
      .circle(0, 0, radius * 0.45)
      .fill({ color, alpha: 0.45 });
  }

  private paintBody(bodyColor: number, trimColor: number): void {
    if (!this.body) return;
    this.body.clear();
    this.body
      .roundRect(-this.w / 2, -this.h / 2, this.w, this.h, this.h * 0.12)
      .fill({ color: bodyColor })
      .stroke({ color: 0x000000, alpha: 0.35, width: 2 });
    const strapH = this.h * 0.08;
    this.body
      .rect(-this.w / 2, -strapH / 2, this.w, strapH)
      .fill({ color: trimColor })
      .stroke({ color: 0x000000, alpha: 0.25, width: 1 });
    const plateW = this.w * 0.18;
    const plateH = this.h * 0.34;
    this.body
      .roundRect(-plateW / 2, -plateH / 2, plateW, plateH, plateH * 0.18)
      .fill({ color: trimColor })
      .stroke({ color: 0x000000, alpha: 0.45, width: 1.5 });
    this.body
      .circle(0, -plateH * 0.05, plateW * 0.18)
      .fill({ color: 0x222222 });
    this.body
      .rect(-plateW * 0.06, -plateH * 0.05, plateW * 0.12, plateH * 0.32)
      .fill({ color: 0x222222 });
  }

  private paintLid(bodyColor: number, trimColor: number): void {
    if (!this.lid) return;
    const lidH = this.h * 0.55;
    this.lid.clear();
    this.lid
      .roundRect(-this.w / 2, -lidH * 0.7, this.w, lidH, lidH * 0.35)
      .fill({ color: bodyColor })
      .stroke({ color: 0x000000, alpha: 0.35, width: 2 });
    const strapH = lidH * 0.16;
    this.lid
      .rect(-this.w / 2, -lidH * 0.3 - strapH / 2, this.w, strapH)
      .fill({ color: trimColor })
      .stroke({ color: 0x000000, alpha: 0.25, width: 1 });
  }
}

/** Build a centred Pixi {@link Sprite} from a square art texture, scaled
 * to {@code targetWidth}. The original commissioned chest PNGs are
 * 1024×1024 — scaling to ~170-220px keeps them well under their
 * native resolution at any HUD-comparable display density. */
function createCenteredSprite(texture: Texture, targetWidth: number): Sprite {
  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5);
  const intrinsic = texture.orig.width || 1;
  sprite.scale.set(targetWidth / intrinsic);
  return sprite;
}
